import React, { useState, useEffect } from 'react';
import { Bell, Check, AlertTriangle, Users, FileText, Database, Trash2, ExternalLink, Merge, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { notificationAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  const [merging, setMerging] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getAll(false, 20);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationAPI.getCount();
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications read:', error);
    }
  };

  const handleAutoMergeClick = async () => {
    try {
      const response = await notificationAPI.previewMerge();
      setMergePreview(response.data);
      setMergeDialogOpen(true);
    } catch (error) {
      toast.error('Kon preview niet laden');
    }
  };

  const handleConfirmMerge = async () => {
    setMerging(true);
    try {
      const response = await notificationAPI.autoMergeDuplicates();
      toast.success(`${response.data.deleted_customers} dubbele klanten samengevoegd!`);
      setMergeDialogOpen(false);
      setMergePreview(null);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      toast.error('Samenvoegen mislukt');
    } finally {
      setMerging(false);
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkRead(notification.id);
    
    // Navigate based on notification type
    if (notification.type === 'new_aanvraag' && notification.data?.aanvraag_id) {
      // Go directly to aanvragen page with the specific aanvraag highlighted
      navigate(`/aanvragen?highlight=${notification.data.aanvraag_id}`);
    } else if (notification.type === 'duplicate_customer' && notification.data?.customers) {
      // Go to the first duplicate customer's detail page
      const firstCustomer = notification.data.customers[0];
      if (firstCustomer?.id) {
        navigate(`/klanten/${firstCustomer.id}`);
      } else {
        navigate('/klanten');
      }
    } else if (notification.type === 'cleaning_photos' && notification.data?.property_id) {
      // Direct naar de documenten van dat adres om de foto's te bekijken
      navigate(`/properties/${notification.data.property_id}?tab=documents`);
    } else if (notification.type === 'installation_photos') {
      // Direct naar de klant om de foto's te bekijken en te downloaden
      if (notification.data?.customer_id) navigate(`/klanten/${notification.data.customer_id}?tab=documents`);
      else navigate('/installaties');
    } else if (notification.type === 'data_error') {
      // Navigate to relevant section based on error type
      if (notification.data?.error_type === 'missing_gps') {
        navigate('/adressen');
      } else {
        navigate('/klanten');
      }
    }
    
    setOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'duplicate_customer':
        return <Users className="w-4 h-4 text-orange-500" />;
      case 'new_aanvraag':
        return <FileText className="w-4 h-4 text-green-500" />;
      case 'data_error':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'backup_complete':
        return <Database className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500';
      case 'normal':
        return 'border-l-blue-500';
      case 'low':
        return 'border-l-gray-400';
      default:
        return 'border-l-gray-400';
    }
  };

  const formatTimeAgo = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Zojuist';
    if (diffMins < 60) return `${diffMins} min geleden`;
    if (diffHours < 24) return `${diffHours} uur geleden`;
    if (diffDays < 7) return `${diffDays} dag${diffDays > 1 ? 'en' : ''} geleden`;
    return date.toLocaleDateString('nl-BE');
  };

  return (
    <>
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-dropdown">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Meldingen</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs"
              onClick={handleMarkAllRead}
            >
              <Check className="w-3 h-3 mr-1" />
              Alles gelezen
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Geen meldingen</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "p-3 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors",
                  getPriorityColor(notification.priority),
                  !notification.read && "bg-primary/5"
                )}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn(
                        "text-sm font-medium truncate",
                        !notification.read && "text-foreground",
                        notification.read && "text-muted-foreground"
                      )}>
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    {notification.type === 'duplicate_customer' && notification.data?.customers && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {notification.data.customers.slice(0, 3).map((customer, idx) => (
                          <span 
                            key={customer.id} 
                            className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-500 rounded"
                          >
                            {customer.first_name} {customer.last_name}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2 space-y-2">
              {/* Auto Merge Button - only for admins when there are duplicates */}
              {user?.role === 'admin' && notifications.some(n => n.type === 'duplicate_customer') && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-xs border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  onClick={handleAutoMergeClick}
                >
                  <Merge className="w-3 h-3 mr-2" />
                  Duplicaten automatisch samenvoegen
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => {
                  navigate('/instellingen');
                  setOpen(false);
                }}
              >
                Alle meldingen beheren
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Auto Merge Confirmation Dialog */}
    <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Merge className="w-5 h-5 text-orange-500" />
            Duplicaten Automatisch Samenvoegen
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                De CRM heeft <strong>{mergePreview?.duplicate_groups || 0} groepen</strong> met dubbele klanten gevonden.
                Er zullen <strong>{mergePreview?.total_to_delete || 0} klanten</strong> worden verwijderd.
              </p>
              
              {mergePreview?.preview && mergePreview.preview.length > 0 && (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  <p className="font-medium text-sm">Preview:</p>
                  {mergePreview.preview.slice(0, 5).map((group, idx) => (
                    <div key={idx} className="p-2 bg-muted/50 rounded text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-muted-foreground">{group.type}:</span>
                        <span className="font-mono">{group.value}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-500">✓ Behouden:</span>
                        <span>{group.keep.name} (score: {group.keep.score})</span>
                      </div>
                      {group.delete.map((del, i) => (
                        <div key={i} className="flex items-center gap-2 text-red-400">
                          <span>✗ Verwijderen:</span>
                          <span>{del.name} (score: {del.score})</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {mergePreview.preview.length > 5 && (
                    <p className="text-xs text-muted-foreground">
                      ... en {mergePreview.preview.length - 5} meer
                    </p>
                  )}
                </div>
              )}
              
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded">
                <p className="text-orange-500 text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Let op
                </p>
                <ul className="text-xs text-orange-400 mt-2 space-y-1">
                  <li>• De klant met de meeste informatie wordt behouden</li>
                  <li>• Ontbrekende data wordt overgenomen van de te verwijderen klant</li>
                  <li>• Adressen en installaties worden verplaatst naar de behouden klant</li>
                  <li>• Er wordt automatisch een backup gemaakt</li>
                </ul>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={merging}>Annuleren</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmMerge}
            disabled={merging || !mergePreview?.total_to_delete}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {merging ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Samenvoegen...
              </>
            ) : (
              <>
                <Merge className="w-4 h-4 mr-2" />
                {mergePreview?.total_to_delete || 0} klanten samenvoegen
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};

export default NotificationBell;
